import { gql, type TypedDocumentNode } from "@apollo/client";
import type { PaginationVariables } from "@/components/Pagination/Pagination";
import type {
  CommissionPayoutRunNode,
  CommissionReleaseBatchLinesConnection,
  CommissionReleaseBatchNode,
  CommissionReleaseBatchStatus
} from "./types";

export interface CommissionReleaseBatchesQueryResponse {
  commissionReleaseBatches: {
    nodes: CommissionReleaseBatchNode[];
    totalCount: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
  };
}

export interface CommissionReleaseBatchesQueryVariables extends PaginationVariables {
  programId?: string;
  status?: CommissionReleaseBatchStatus;
}

export const COMMISSION_RELEASE_BATCHES_QUERY: TypedDocumentNode<
  CommissionReleaseBatchesQueryResponse,
  CommissionReleaseBatchesQueryVariables
> = gql`
  query CommissionReleaseBatches(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $programId: ID
    $status: BackofficeCommissionReleaseBatchStatus
  ) {
    commissionReleaseBatches(
      first: $first
      after: $after
      last: $last
      before: $before
      programId: $programId
      status: $status
    ) {
      nodes {
        id
        status
        batchType
        selectionCount
        requestedLineIds
        exceptionCount
        depositFileKey
        payoutRunCount
        createdAt
        program {
          id
          name
          slug
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export interface CommissionReleaseBatchQueryResponse {
  commissionReleaseBatch: CommissionReleaseBatchNode & {
    lines: CommissionReleaseBatchLinesConnection;
    payoutRuns: CommissionPayoutRunNode[];
  };
}

export interface CommissionReleaseBatchQueryVariables {
  id: string;
  linesFirst?: number;
  linesAfter?: string;
}

export const COMMISSION_RELEASE_BATCH_QUERY: TypedDocumentNode<
  CommissionReleaseBatchQueryResponse,
  CommissionReleaseBatchQueryVariables
> = gql`
  query CommissionReleaseBatch($id: ID!, $linesFirst: Int, $linesAfter: String) {
    commissionReleaseBatch(id: $id) {
      id
      status
      batchType
      selectionCount
      lineCount
      requestedLineIds
      exceptionCount
      depositFileKey
      totalAmountCents
      payoutRunCount
      exportedAt
      createdAt
      program {
        id
        name
        slug
      }
      lines(first: $linesFirst, after: $linesAfter) {
        nodes {
          id
          person {
            id
            displayName
            pgaId
          }
          amountCents
          fundingStatus
          payoutEligibilityStatus
          holdReasons
          lineKind
          receivedAt
          payableAt
          paidOutAt
          expiredAt
          facilityName
          fundingBatch {
            id
            status
          }
          releaseBatch {
            id
            status
          }
          splitLines {
            id
          }
          campaign {
            id
            editionYear
            partner {
              id
              name
            }
            program {
              id
              name
            }
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
      payoutRuns {
        id
        status
        pgaId
        personDisplayName
        person {
          id
          displayName
          pgaId
        }
        periodSummary
        lineItems {
          id
          amountCents
          line {
            id
            amountCents
          }
        }
        amountCents
        lineCount
      }
    }
  }
`;
